import NextImage from 'next/image';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../helpers/analytics/trackEvent';
import { EventMykissCardClicked } from '../../../interfaces/events';
import { useExplore } from '../../../views/Explore/context/ExploreContext';
import { ChevronRightIcon } from '../../Icons/ChevronRightIcon';
import { FilledWarningIcon } from '../../Icons/FilledWarningIcon';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import { Text } from '../../Text/Text';

interface IProps {
  name: string;
  speciesId: number;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  className?: string;
  imageSrc?: string;
  imageContainerClassName?: string;
  prohibited?: boolean;
}

export const SingleSpeciesCard = ({
  speciesId,
  name,
  clickEventSource,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  imageContainerClassName = '',
  prohibited,
  imageSrc,
}: IProps) => {
  const { saveParamsInUrl } = useExplore();
  const { currentPage } = useAnalytics();
  const handleClick = (): void => {
    saveParamsInUrl({
      drawerView: 'regulation',
      drawerViewId: speciesId.toString(),
    });

    void trackEvent({
      name: 'mykiss_card_clicked',
      properties: {
        source: clickEventSource,
        target: 'species',
        target_id: speciesId.toString(),
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      },
    });
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleClick}
      className={`hover:bg-deep-sea/10 bg-sea-foam active:bg-sea-foam/15 mb-2 w-full cursor-pointer rounded-md md:rounded-lg ${className}`}
      data-testid="singleSpeciesCard"
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center">
        <div
          className={`w-col-1 sm:w-col-3 m-3 flex items-center justify-center ${imageContainerClassName}`}
        >
          {imageSrc ? (
            <NextImage
              className="size-full object-cover"
              src={imageSrc}
              alt={name}
              width={92}
              height={60}
              data-testid="SpeciesImage"
            />
          ) : (
            <FishCatchesIcon size={55} />
          )}
        </div>
        <div className="w-col-3 sm:w-col-6 md:w-col-5 flex">
          <div>
            <Text variant="sm" className="text-trench mb-0">
              {name}
            </Text>
            {prohibited && (
              <div className="flex items-center">
                <FilledWarningIcon />
                <Text variant="sm" className="text-error-shade mb-0 ml-1">
                  Prohibited
                </Text>
              </div>
            )}
          </div>
        </div>
        <div className="text-abyss mr-1.5">
          <ChevronRightIcon />
        </div>
      </div>
    </div>
  );
};
